/**
 * Delta compiler engine — O(changed) editing at any workspace size.
 *
 * Holds the compiled workspace as a PERSISTENT KEYED STORE rather than
 * rebuilding a flat `ResolvedModel` on every edit. Each top-level declaration
 * gets a CONTENT-DERIVED stable id (`path · kind · name · hash(sourceSlice)`),
 * so an edit to one module in a 20-module file changes exactly one id and
 * re-resolves exactly one declaration — independent of workspace size.
 *
 * Pipeline coupling (see docs/.../2026-06-04-delta-compiler-engine.md):
 *   - resolve  — registry-coupled: a module depends only on its own AST + the
 *     type registry. Edit one module → re-resolve one. Edit a type → rebuild
 *     registry + re-resolve all (rare, correct).
 *   - assemble (in-clause tree + scoped processes/subprocesses) — runs over the
 *     keyed store at materialise time. Shares `assembleModel` with the cold
 *     `resolveFile` so both paths produce byte-identical models.
 *   - cascade / graph / validate — driven from the assembled model (S2-S4 make
 *     these delta-driven; S0 runs them whole but correct).
 *
 * Correctness invariant (the spec): `state()` === a cold `buildViewerState`
 * over the merged VFS, for every reachable edit sequence. The equivalence
 * harness fuzzes this.
 */
import type {
  Diagnostic,
  ExtensionDecl,
  ModuleDecl,
  OnInclusion,
  ParsedFile,
  ProcessDecl,
  ProcessStep,
  SubprocessDecl,
  TypeDecl,
  QualifiedName,
  AspectEntry,
  SourceSpan,
  Value,
} from "./types.js";
import type { LensDecl, TestDecl, ViewDeclV2 } from "./v010-ast.js";
import { makeDiag } from "./diagnostics/catalog.js";
import { buildDecorationRegistry } from "./decorations.js";
import type { DecorationDecl, DecorationInstance, DecorationRegistry } from "./decorations.js";
import { validateDecorations } from "./decoration-validate.js";
import {
  assembleModel,
  buildRegistry,
  resolveModule,
  applyExtensionBlocks,
  applyCascade,
  type ResolvedModel,
  type ResolvedModule,
  type ResolvedInterface,
  type ResolvedSurface,
  type ResolvedType,
  type ResolvedTypeRegistry,
  type AssemblyOutputs,
  type AssemblyCacheHolder,
} from "./resolver.js";
import { buildIndexes, buildEdges, type DependencyGraph, type DependencyEdge } from "./graph.js";
import { forEachCallHop } from "./process-ir-walk.js";
import type { Flow } from "./process-model.js";
import type { PresenceInfo } from "./presence.js";
import { moduleSuffixes, ifaceSpellings } from "./hoist-index.js";
import type { ProcessModelCache } from "./process-model-builder.js";
import type { ProcessModel, SubprocessModel } from "./process-model.js";
import {
  localModuleDiagnostics,
  typeNameDiagnostics,
  aspectRefDiagnostics,
  descriptionDiagnosticsScoped,
  validateProcess,
  validateSubprocess,
  validateProcessOwners,
  validatePlanePresence,
  validateAspectPlacement,
  validatePartialPresence,
  validateInclusions,
  validateLatencyFields,
  validateLatencyCoverage,
  validateLifecycleReferences,
  buildSymbolIndex,
} from "./validation.js";
import { findProcessRoleConflicts } from "./stubs.js";
import { collectDocAux, type DescriptionIndex, type DescriptionDeclRef } from "./description.js";

function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(36);
}

/** Order-insensitive content key of a file's `on { … }` inclusion blocks (target
 *  + planes only, no spans) so a whitespace-shifting edit elsewhere doesn't read
 *  as an inclusion change. Sorted so member order within a block is irrelevant. */
function onInclusionsKey(list: readonly OnInclusion[]): string {
  return list
    .map((i) => `${i.target.join(".")}@${i.planes.map((p) => p.join(".")).sort().join(",")}`)
    .sort()
    .join(";");
}

/** Content signature of a resolved kind — what a module's resolution depends
 *  on (base type, parent chain, stamped template body). Source spans excluded
 *  so an unrelated type edit that merely shifts another kind's byte offsets
 *  does NOT spuriously invalidate that kind's modules. */
function kindSig(t: ResolvedType): string {
  return JSON.stringify({ b: t.baseType, c: t.chain, s: t.stampedBody }, (k, v) =>
    k === "source" || k.endsWith("Source") || k === "file" ? undefined : v
  );
}

/** Bare names of kinds whose stamped signature changed (added / removed /
 *  edited) between two registries. A module re-resolves iff its kind name is
 *  in this set — exact because `resolveModule` depends only on its kind's
 *  signature. Handles subtype ripple (a parent edit changes every descendant's
 *  stampedBody) and unknown↔known transitions (add/remove of a kind id). */
function affectedKindNames(
  oldReg: ResolvedTypeRegistry,
  newReg: ResolvedTypeRegistry
): Set<string> {
  const names = new Set<string>();
  for (const [id, t] of newReg.byId) {
    const o = oldReg.byId.get(id);
    if (!o || kindSig(o) !== kindSig(t)) names.add(t.name);
  }
  for (const [id, t] of oldReg.byId) {
    if (!newReg.byId.has(id)) names.add(t.name);
  }
  return names;
}

/** Structural signature of a resolved module subtree — the part the symbol /
 *  subprocess indexes depend on (names, nesting, interfaces, surfaces, body
 *  subprocesses, re-parent target). Excludes field values + descriptions, so a
 *  pure value edit produces the SAME signature (no index invalidation). */
function structuralSig(m: ResolvedModule, parentPath: string): string {
  return fnv1a(sigString(m, parentPath));
}
function sigString(m: ResolvedModule, parent: string): string {
  // v0.9.0 §1.6: a default interface (unnamed + braced) resolves a bare module
  // callee, so its presence is structural — fold `unnamed`/`hasBody` into the
  // signature. Otherwise swapping `rest_read Interface` (a named leaf) for
  // `interface {}` (same derived name, a DEFAULT) leaves the sig unchanged and
  // the process IR cache serves a stale callee classification (cold ≠ delta).
  const ifaces = m.interfaces
    .map((i) => `${i.name}${i.decl.unnamed ? "~u" : ""}${i.decl.hasBody ? "~b" : ""}`)
    .sort()
    .join(",");
  const surfaces = m.surfaces.map(surfaceSig).sort().join(";");
  const subs = (m.body.subprocesses ?? []).map((s) => s.name).sort().join(",");
  const kids = m.modules.map((c) => sigString(c, m.name)).join("|");
  // v0.9.0 §1.5: membership instances (`aspect … as active`, rosters) are a
  // cross-decl input to plane-process IR (`instancesAt`), so a change to them
  // must re-resolve dependent processes → fold into the structural signature.
  return `${parent}>${m.name}|if:${ifaces}|fc:${surfaces}|sp:${subs}|ms:${membershipSig(m)}|ch:[${kids}]`;
}

/** Membership refs of a module (member-side ref aspects + place-side rosters),
 *  serialized order-insensitively. EVERY ref-valued membership matters, not only
 *  `as`-named ones: an `as` instance drives `instancesAt` (process IR), but even a
 *  plain `aspect broker: OurKafka` PULLS the referenced place onto the key's plane
 *  (presence.ts membership pull) — a cross-module presence change that feeds
 *  `WireModule.planes`. Folding plain refs in keeps the structural sig a superset
 *  of presence inputs, so the serialize version bumps and the pulled place's
 *  cached wire can't go stale (cold ≠ delta). */
function membershipSig(m: ResolvedModule): string {
  const parts: string[] = [];
  const suffix = (inst: string | undefined) => (inst ? `~${inst}` : "");
  // Mirror presence.refItems: a bare module ref parses as an `identifier` (the
  // resolver rewrites it to `ref` only later), and presence pulls the place for
  // BOTH forms. Missing the identifier form is exactly what let a plain
  // `aspect broker: OurKafka` slip through as non-structural.
  const refInst = (key: string, v: Value | undefined): void => {
    if (!v) return;
    if (v.kind === "identifier") parts.push(`${key}=${v.text}`);
    else if (v.kind === "ref") parts.push(`${key}=${v.path.join(".")}${suffix(v.instance)}`);
    else if (v.kind === "list") for (const it of v.items) refInst(key, it);
  };
  for (const l of m.decl.body.aspects) refInst(l.path.join("."), l.value);
  for (const r of m.decl.body.rosters ?? []) {
    for (const e of r.entries) {
      parts.push(`${r.key.join(".")}for=${e.path.join(".")}${suffix(e.instance)}`);
    }
  }
  return parts.sort().join(",");
}

/** Presence-input signature of a resolved module subtree — everything
 *  `computePresence` reads from a module that `structuralSig` does not already
 *  witness in the same shape: born-on planes + body `alsoOn` (absent from
 *  `sigString` entirely), roster keys (declaring a roster pulls placehood even
 *  with zero entries), and the RESOLVED body's ref-valued aspects + roster
 *  entries in DECLARATION order — presence walks `m.body.aspects` (template-
 *  stamped + refined) where `membershipSig` reads the raw decl and sorts, and
 *  `instancesAt` is last-wins, so order is content. Names/nesting are folded in
 *  too, so any module add/remove/rename/move — which shifts `resolveRef`'s
 *  unique-suffix hoisting universe — also reads as a presence-input change.
 *  Gates ONLY the A1 presence cache (`presenceDirty`); deliberately not part of
 *  `structuralSig`, so a pure plane edit doesn't bump `symStructVersion` and
 *  spuriously drop the IR / symbol caches. */
function planeSigString(m: ResolvedModule, parent: string): string {
  const planes = (ps: readonly QualifiedName[] | undefined): string =>
    (ps ?? []).map((p) => p.join(".")).join(",");
  const refs: string[] = [];
  const refItems = (key: string, v: Value | undefined): void => {
    if (!v) return;
    if (v.kind === "identifier") refs.push(`${key}=${v.text}`);
    else if (v.kind === "ref") refs.push(`${key}=${v.path.join(".")}${v.instance ? `~${v.instance}` : ""}`);
    else if (v.kind === "list") for (const it of v.items) refItems(key, it);
  };
  for (const l of m.body.aspects) refItems(l.path.join("."), l.value);
  const rosters = (m.decl.body.rosters ?? [])
    .map(
      (r) =>
        `${r.key.join(".")}for=${r.entries
          .map((e) => `${e.path.join(".")}${e.instance ? `~${e.instance}` : ""}`)
          .join(",")}`
    )
    .join(";");
  // §7 partial presence: the block's PLANE and which members ride it are
  // presence inputs that nothing else witnesses. The declarations are folded
  // into the resolved body (so `structuralSig` sees them), but the plane they
  // ride is only ever read off `decl.body.partialOn` — retyping `on obs { … }`
  // as `on deploy { … }` changes no other signature, and without this the A1
  // presence cache would serve the old plane forever.
  const partial = (m.decl.body.partialOn ?? [])
    .map((pp) => {
      const decls = [
        ...pp.body.interfaces.map((i) => i.name),
        ...pp.body.surfaces.map((s) => s.name),
        ...pp.body.fields.map((f) => f.path.join(".")),
        ...pp.body.modules.map((c) => c.name),
        ...pp.body.processes.map((p) => p.name),
      ];
      const rs = (pp.refs ?? []).map((r) => r.path.join("."));
      return `${pp.plane.join(".")}{${decls.join(",")}}(${rs.join(",")})`;
    })
    .join(";");
  const kids = m.modules.map((c) => planeSigString(c, m.name)).join("|");
  return `${parent}>${m.name}|on:${planes(m.decl.onPlanes)}|ao:${planes(m.decl.body.alsoOn)}|ms:${refs.join(",")}|ro:${rosters}|pp:${partial}|ch:[${kids}]`;
}

/** Every `as`-instance NAME an aspect value declares (`Euro as active` → "active"). */
function collectInstanceNames(v: Value | undefined, add: (name: string) => void): void {
  if (!v) return;
  if (v.kind === "ref" && v.instance) add(v.instance);
  else if (v.kind === "list") for (const it of v.items) collectInstanceNames(it, add);
}
function surfaceSig(f: { name: string; interfaces: readonly { name: string }[]; surfaces: readonly unknown[] }): string {
  const ifaces = f.interfaces.map((i) => i.name).sort().join(",");
  const sub = (f.surfaces as Parameters<typeof surfaceSig>[0][]).map(surfaceSig).sort().join(";");
  return `${f.name}(if:${ifaces};fc:${sub})`;
}

/** True if a module subtree declares any body-nested process — an anonymous
 *  bare-step process (§4.13) or a named `process { … }`. These are lifted into
 *  the model's flat `processes` array, so when such a module is added, removed,
 *  or content-edited the process-validation + graph-edge caches must invalidate
 *  (otherwise a bare-step edit keeps stale diagnostics/edges → cold ≠ delta). */
function declHasBodyProcesses(decl: ModuleDecl): boolean {
  if (decl.body.processes.length > 0) return true;
  if ((decl.body.subprocesses?.length ?? 0) > 0) return true;
  for (const sub of decl.body.modules) if (declHasBodyProcesses(sub)) return true;
  return false;
}

/** True if a module subtree declares any body-nested view (v0.10 §5.2). These
 *  are lifted into the model's flat `views` array during assembly, so a module
 *  carrying one must mark the view set dirty (assembly cache + description
 *  tail) exactly like a top-level `view` decl — otherwise cold ≠ delta. */
function declHasBodyViews(decl: ModuleDecl): boolean {
  if ((decl.body.views?.length ?? 0) > 0) return true;
  for (const sub of decl.body.modules) if (declHasBodyViews(sub)) return true;
  return false;
}

/** True if a RESOLVED module subtree carries any ASPECT cascade drop
 *  (`aspect { drop k }`). Read from the resolved body (not the raw decl)
 *  because kind templates stamp body content onto instances. Field drops
 *  don't feed `validateCascadeDrops`, so they don't count. Gates the A1
 *  assembly cache: a drop's ASPECT_DROP_NOT_INHERITED diag depends on
 *  ancestor-aspect VALUE-definedness, which no structural/plane signature
 *  witnesses — so while any aspect drop exists, module changes conservatively
 *  recompute the assembly passes. */
function subtreeHasAspectDrops(m: ResolvedModule): boolean {
  if ((m.body.cascadeDrops ?? []).some((d) => d.kind === "aspect")) return true;
  return m.modules.some(subtreeHasAspectDrops);
}

// === S6: per-root reverse-dependency machinery (structural edits O(changed)) ===
// The symbol surface a module subtree DEFINES + the symbols it REFERENCES are
// extracted per top-level root, identity-keyed. `state()` diffs roots by
// identity, patches a refcounted global symbol index (so a structural edit
// flips only the symbols that actually appeared/disappeared), then re-validates
// only the changed roots + the roots that reference a flipped symbol — instead
// of every declaration.

/** Symbols a module subtree DEFINES — must match `buildSymbolIndex` membership
 *  (qualified path + short name per module; every interface SPELLING: the full
 *  path, its hoistable owner-suffixes, and the §4.11 elided module-prefix forms)
 *  plus the explicit ids (for `[[#id]]` reference gating). Interface spellings
 *  matter for the flip diff: a process that wrote `Orders.create` (elided) or
 *  `Api.create` (suffix) references THAT token, so declaring/removing the leaf
 *  must flip the same key or the dependent process keeps stale edges/diags. */
function extractSymbols(root: ResolvedModule): { mods: string[]; ifaces: string[]; ids: string[] } {
  const mods: string[] = [];
  const ifaces: string[] = [];
  const ids: string[] = [];
  const visitF = (f: ResolvedSurface, parentSegs: readonly string[], moduleSegs: readonly string[]): void => {
    const segs = [...parentSegs, f.name];
    for (const sub of f.surfaces) visitF(sub, segs, moduleSegs);
    for (const i of f.interfaces) ifaces.push(...ifaceSpellings(moduleSegs, segs, i.name));
  };
  const visitM = (m: ResolvedModule, parentSegs: readonly string[]): void => {
    const segs = [...parentSegs, m.name];
    mods.push(segs.join("."));
    mods.push(m.name);
    if (m.id) ids.push(m.id);
    for (const f of m.surfaces) visitF(f, segs, segs);
    for (const i of m.interfaces) ifaces.push(...ifaceSpellings(segs, segs, i.name));
    for (const c of m.modules) visitM(c, segs);
  };
  visitM(root, []);
  return { mods, ifaces, ids };
}

function descRefsOf(text: string, paths: string[], ids: string[]): void {
  const re = /\[\[([^\]\n]+)\]\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const inner = (m[1] ?? "").trim();
    if (inner.startsWith("#")) ids.push(inner.slice(1).trim());
    else paths.push(inner);
  }
}

/** Symbols a module subtree REFERENCES — description `[[refs]]` and bare aspect
 *  refs. If any of these flips presence, the subtree's
 *  cross-ref validation must re-run. */
function extractRefs(root: ResolvedModule): { paths: string[]; ids: string[] } {
  const paths: string[] = [];
  const ids: string[] = [];
  const visitBody = (body: { descriptions: readonly { text: string }[]; aspects: readonly AspectEntry[] }): void => {
    for (const d of body.descriptions) descRefsOf(d.text, paths, ids);
    for (const l of body.aspects) {
      if (!l.value) continue;
      if (l.value.kind === "identifier") paths.push(l.value.text);
      else if (l.value.kind === "list") {
        // Multi-ref lists (`aspects.owner: A, B`): every identifier item is a
        // bare module ref that must re-validate when the referent flips.
        for (const it of l.value.items) if (it.kind === "identifier") paths.push(it.text);
      }
    }
  };
  const visitI = (i: ResolvedInterface): void => {
    for (const d of i.body.descriptions) descRefsOf(d.text, paths, ids);
  };
  const visitF = (f: ResolvedSurface): void => {
    visitBody(f.body);
    for (const i of f.interfaces) visitI(i);
    for (const sub of f.surfaces) visitF(sub);
  };
  const visitM = (m: ResolvedModule): void => {
    visitBody(m.body);
    for (const i of m.interfaces) visitI(i);
    for (const f of m.surfaces) visitF(f);
    for (const c of m.modules) visitM(c);
  };
  visitM(root);
  return { paths, ids };
}

/** True if any referenced symbol (by any path prefix, or by id) is in the
 *  flipped set. Prefix-match because a ref to `A.B.Op` is invalidated when its
 *  owning module `A` or `A.B` changes. Tokens are namespaced (`m:`/`i:`/`#:`). */
function refsAffected(refs: { paths: readonly string[]; ids: readonly string[] }, flipped: ReadonlySet<string>): boolean {
  if (flipped.size === 0) return false;
  for (const p of refs.paths) {
    let prefix = "";
    for (const seg of p.split(".")) {
      prefix = prefix ? `${prefix}.${seg}` : seg;
      if (flipped.has(`m:${prefix}`) || flipped.has(`i:${prefix}`)) return true;
    }
  }
  for (const id of refs.ids) if (flipped.has(`#:${id}`)) return true;
  return false;
}

/** Symbol tokens for the callers the IR RESOLVED (§4.13.4) — an omitted head, a
 *  `|` fan and a `\` snake name no module in the text, so the written walk below
 *  can't see them. Missing a token means a symbol flip skips re-validation and
 *  leaves a stale diagnostic, so both sets of tokens are collected. */
function resolvedCallerRefs(body: Flow | undefined): string[] {
  const out: string[] = [];
  forEachCallHop(body, (hop) => {
    let prefix = "";
    for (const seg of hop.caller) {
      prefix = prefix ? `${prefix}.${seg}` : seg;
      out.push(`m:${prefix}`, `i:${prefix}`);
    }
  });
  return out;
}

/** All symbol tokens a process (or subprocess) references (step from = module,
 *  to = interface), with prefixes, namespaced — for gating whole-set
 *  re-validation / graph rebuild. Recurses into nested subprocess bodies so a
 *  symbol reached only through a `do` chain still gates correctly. */
function extractProcRefs(proc: {
  readonly steps: readonly ProcessStep[];
  readonly subprocesses?: readonly SubprocessDecl[];
}): string[] {
  const out: string[] = [];
  const addPath = (segs: readonly string[]): void => {
    let prefix = "";
    for (const seg of segs) {
      prefix = prefix ? `${prefix}.${seg}` : seg;
      out.push(`m:${prefix}`, `i:${prefix}`);
    }
  };
  const walk = (steps: readonly ProcessStep[]): void => {
    for (const step of steps) {
      switch (step.kind) {
        case "simple": addPath(step.from); addPath(step.to); break;
        case "each":
          walk(step.steps);
          if (step.retry) {
            walk(step.retry.attempt);
            for (const c of step.retry.catches) walk(c.steps);
            if (step.retry.elseSteps) walk(step.retry.elseSteps);
          }
          break;
        case "if": for (const b of step.branches) walk(b.steps ?? []); if (step.elseSteps) walk(step.elseSteps); break;
        case "select": for (const c of step.cases) walk(c.steps); break;
        case "try": walk(step.trySteps); for (const c of step.catches) walk(c.steps); break;
        case "parallel":
          for (const b of step.branches) walk(b.steps);
          if (step.fanOut) walk(step.fanOut.steps);
          if (step.elseSteps) walk(step.elseSteps); // §2.2 timeout branch
          break;
        case "note": case "prose": case "do": case "go": case "fail": case "finish": break;
        case "reversible":
          for (const item of step.items) {
            if (item.forward) {
              if (item.forward.kind === "block") walk(item.forward.steps);
              else walk([item.forward]);
            }
            const a = item.unwind?.action;
            if (a?.kind === "simple") { addPath(a.from); addPath(a.to); }
            else if (a?.kind === "block") walk(a.steps);
          }
          break;
        case "owner_block": walk(step.steps); break;
        case "await": break;
        default: { const _x: never = step; void _x; }
      }
    }
  };
  walk(proc.steps);
  for (const sub of proc.subprocesses ?? []) out.push(...extractProcRefs(sub));
  return out;
}

type SpaceOf = (file: string | undefined) => QualifiedName | undefined;

/** Description-index entries a module subtree contributes — replicates
 *  `buildDescriptionIndex`'s `visitModule` (module + surfaces + interfaces,
 *  recursively) so the incremental index is byte-identical to the cold one. */
function descRefsOfModuleSubtree(root: ResolvedModule, spaceOf: SpaceOf): DescriptionDeclRef[] {
  const out: DescriptionDeclRef[] = [];
  const visitI = (i: ResolvedInterface, parent: string, inherited: QualifiedName | undefined): void => {
    out.push({ name: i.name, qualifiedName: `${parent}.${i.name}`, typeName: i.typeName, space: spaceOf(i.decl.source?.file) ?? inherited });
  };
  const visitF = (f: ResolvedSurface, parent: string, inherited: QualifiedName | undefined): void => {
    const q = `${parent}.${f.name}`;
    const space = spaceOf(f.decl.source?.file) ?? inherited;
    out.push({ name: f.name, qualifiedName: q, typeName: f.typeName, space });
    for (const sub of f.surfaces) visitF(sub, q, space);
    for (const i of f.interfaces) visitI(i, q, space);
  };
  const visitM = (m: ResolvedModule, parent: string): void => {
    const q = parent ? `${parent}.${m.name}` : m.name;
    const space = spaceOf(m.decl.source?.file);
    out.push({ id: m.id, name: m.name, qualifiedName: q, typeName: m.typeName, space });
    for (const f of m.surfaces) visitF(f, q, space);
    for (const i of m.interfaces) visitI(i, q, space);
    for (const c of m.modules) visitM(c, q);
  };
  visitM(root, "");
  return out;
}

/** Non-module description-index entries (types, subprocesses, processes, views)
 *  — the index "tail", small relative to the module count. Replicates the
 *  corresponding tail loops in `buildDescriptionIndex`. */
function tailDescRefs(model: ResolvedModel, spaceOf: SpaceOf): DescriptionDeclRef[] {
  const out: DescriptionDeclRef[] = [];
  for (const t of model.types) {
    out.push({ id: t.id, name: t.name, qualifiedName: t.name, typeName: t.parentType, space: spaceOf(t.source?.file) });
  }
  for (const sp of model.subprocesses) {
    const q = sp.ownerPath && sp.ownerPath.length > 0 ? `${sp.ownerPath.join(".")}.${sp.name}` : sp.name;
    out.push({ id: sp.id, name: sp.name, qualifiedName: q, typeName: "subprocess", space: spaceOf(sp.source?.file) });
  }
  for (const p of model.processes) {
    const q = p.ownerPath && p.ownerPath.length > 0 ? `${p.ownerPath.join(".")}.${p.name}` : p.name;
    out.push({ id: p.id, name: p.name, qualifiedName: q, typeName: "process", space: spaceOf(p.source?.file) });
  }
  for (const v of model.views) {
    const q = v.ownerPath && v.ownerPath.length > 0 ? `${v.ownerPath.join(".")}.${v.name}` : v.name;
    out.push({ id: v.id, name: v.name, qualifiedName: q, typeName: "view", space: spaceOf(v.source?.file) });
  }
  return out;
}

/** Deterministic diagnostic ordering (by source position, then code/message) so
 *  the incremental path — which emits per-decl on the dirty set — yields a
 *  stable order regardless of recompute sequence. */
function sortDiags(diags: Diagnostic[]): Diagnostic[] {
  return diags.slice().sort((a, b) => {
    const af = a.source?.file ?? "", bf = b.source?.file ?? "";
    if (af !== bf) return af < bf ? -1 : 1;
    const ao = a.source?.start?.offset ?? 0, bo = b.source?.start?.offset ?? 0;
    if (ao !== bo) return ao - bo;
    const ac = a.code ?? "", bc = b.code ?? "";
    if (ac !== bc) return ac < bc ? -1 : 1;
    return a.message < b.message ? -1 : a.message > b.message ? 1 : 0;
  });
}

const EMPTY_GRAPH: DependencyGraph = {
  edges: [],
  modulesByPath: new Map(),
  interfacesByPath: new Map(),
  surfacesByPath: new Map(),
  modulesById: new Map(),
  pathOfModule: new Map(),
};

const EMPTY_MODEL: ResolvedModel = {
  types: [],
  registry: { kinds: new Map(), byId: new Map() },
  modules: [],
  processes: [],
  subprocesses: [],
  views: [],
};

interface DeclEntry {
  readonly id: string;
  readonly kind: string; // "type" | "module" | "process" | "subprocess" | "view"
  readonly name: string;
  readonly file: string;
  readonly decl: unknown;
}

/** What a `setFile` changed, in declaration ids. A content edit to one decl
 *  shows up as (its old id removed, new id added) — same name, new hash. */
export interface CompileDelta {
  readonly added: readonly string[];
  readonly removed: readonly string[];
  /** True when a `type` decl changed → the registry was rebuilt and every
   *  module re-resolved (type templates stamp every instance). */
  readonly registryRebuilt: boolean;
  /** Modules actually re-run through `resolveModule` this edit (the cost). */
  readonly modulesResolved: number;
  /** Qualified declared paths (`Parent.Child` / `Name`) of the top-level modules
   *  added, removed, or re-resolved this edit. A board adapter maps each to its
   *  enclosing space id and calls `BoardEngine.invalidate` — only those spaces
   *  re-layout (S5). Decoupled from the board's space-id format on purpose. */
  readonly changedModulePaths: readonly string[];
  /** The symbol / subprocess surface changed (names, interfaces, nesting,
   *  re-parent) — not just field values. Index-coupled consumers (validate,
   *  graph) had to re-run; a board may invalidate more broadly. */
  readonly structural: boolean;
}

/** Full compiled state — equivalent to viewer-core's `buildViewerState`. */
export interface CompiledState {
  /** Cascaded model (inherited fields stamped) — what consumers render. */
  readonly model: ResolvedModel;
  /** Pre-cascade model — for diffs that need the as-written values. */
  readonly rawModel: ResolvedModel;
  readonly graph: DependencyGraph;
  readonly diagnostics: readonly Diagnostic[];
}

/**
 * Source → AST parser, injected so `core` does not depend on `@archlang/engine`
 * (which itself depends on `core` — importing it here would invert the layering
 * and create a build cycle). Pass `parse` from `@archlang/engine`. The return
 * type is widened structurally: only `.file` is consumed here.
 */
export type ParseFn = (
  source: string,
  file?: string
) => {
  readonly file: ParsedFile;
  readonly errors?: readonly {
    readonly message: string;
    readonly span?: SourceSpan;
    // Catalog fields ride along so parse/lex errors localise like semantic
    // diagnostics (surfaced via setFile below).
    readonly code?: string;
    readonly msgKey?: string;
    readonly params?: Readonly<Record<string, unknown>>;
  }[];
};

export interface IncrementalCompilerOptions {
  /** Source parser (e.g. `@archlang/engine`'s `parse`). Required: the engine
   *  turns file content into an AST on every `setFile`. */
  readonly parse: ParseFn;
  /** File URI → enclosing space (v0.5). Absent → every decl is root (flat),
   *  identical to a single-space package. */
  readonly spaces?: ReadonlyMap<string, QualifiedName>;
  /** Wall-clock instant the derived timeline places `now` against — see
   *  {@link IncrementalCompiler.setNow}. */
  readonly nowMs?: number;
}

/**
 * Per-decl IR memo for the incremental path. Validity = the decl object is
 * unchanged AND the symbol STRUCTURE version (which gates callee classification)
 * hasn't bumped since the entry was cached. Keyed by `(decl identity, ownerKey)`
 * — a WeakMap on the decl so replaced decls evict automatically (no leak); the
 * ownerKey distinguishes a template-stamped subprocess built under different
 * owners. A process-body or value edit replaces only the edited decl object and
 * never bumps the version, so every unchanged process reuses its IR.
 */
class IncrementalIRCache implements ProcessModelCache {
  private readonly procs = new WeakMap<ProcessDecl, Map<string, { model: ProcessModel; v: number }>>();
  private readonly subs = new WeakMap<SubprocessDecl, Map<string, { model: SubprocessModel; v: number }>>();
  constructor(private readonly versionOf: () => number) {}

  process(decl: ProcessDecl, ownerKey: string, build: () => ProcessModel): ProcessModel {
    return this.memo(this.procs, decl, ownerKey, build);
  }
  subprocess(decl: SubprocessDecl, ownerKey: string, build: () => SubprocessModel): SubprocessModel {
    return this.memo(this.subs, decl, ownerKey, build);
  }

  private memo<K extends object, V>(
    store: WeakMap<K, Map<string, { model: V; v: number }>>,
    decl: K,
    ownerKey: string,
    build: () => V,
  ): V {
    const v = this.versionOf();
    let byOwner = store.get(decl);
    const hit = byOwner?.get(ownerKey);
    if (hit && hit.v === v) return hit.model;
    const model = build();
    if (!byOwner) { byOwner = new Map(); store.set(decl, byOwner); }
    byOwner.set(ownerKey, { model, v });
    return model;
  }
}

export class IncrementalCompiler {
  /** First-seen sequence per file — gives a STABLE merged-file order for
   *  materialisation + diagnostics, matching a cold concat of the same files.
   *  Never reassigned: a file that empties then refills keeps its position. */
  private fileSeq = new Map<string, number>();
  private seqCounter = 0;
  private fileDeclIds = new Map<string, string[]>();
  private entryById = new Map<string, DeclEntry>();
  private typeDeclIds = new Set<string>();
  private extensionDeclIds = new Set<string>();
  /** IN_TARGET_NOT_FOUND (and future) diagnostics from `applyExtensionBlocks`. */
  private extensionDiags: Diagnostic[] = [];
  private resolvedById = new Map<string, ResolvedModule>();
  private resolveDiagsById = new Map<string, readonly Diagnostic[]>();
  /** Bare kind name → ids of module decls referencing it. Lets a type edit
   *  re-resolve only the modules whose kind actually changed (S1), in O(affected). */
  private modulesByTypeName = new Map<string, Set<string>>();
  private registry: ResolvedTypeRegistry | undefined;
  private registryDiags: Diagnostic[] = [];
  /** Parse/lex error diagnostics, keyed by file path (per-file so an edit
   *  replaces only that file's parse errors). */
  private parseDiagsByFile = new Map<string, Diagnostic[]>();
  /** v0.9.0 §1.2: per-file top-level `on <plane> { … }` inclusion blocks. Not
   *  declarations, so tracked separately; flattened into `assembleModel` so the
   *  incremental presence (and its plane-process instance actors) matches cold. */
  private onInclusionsByFile = new Map<string, readonly OnInclusion[]>();
  private spaces?: ReadonlyMap<string, QualifiedName>;
  /** Wall-clock instant the derived timeline places `now` against. Must match
   *  what the cold path was handed, or the two place `now` differently. */
  private nowMs?: number;
  /** Ambient imported types (from `use … from …`), not owned by any file. */
  private imports: readonly TypeDecl[] = [];

  // --- S3 incremental validation state ---
  /** Refcounted multiset of per-top-level-module STRUCTURAL signatures — drives
   *  the delta's `structural` flag + graph-edge invalidation (coarse). */
  private structCounts = new Map<string, number>();
  private structSigById = new Map<string, string>(); // module entry id → its sig
  /** Module entry id → its presence-input signature (`planeSigString`). */
  private planeSigById = new Map<string, string>();
  private symStructVersion = 0; // bumped on any structural flip
  /** Per-decl IR memo — reuses unchanged processes' IR across snapshots, keyed
   *  on decl identity + `symStructVersion` (so a structural flip invalidates). */
  private readonly irCache = new IncrementalIRCache(() => this.symStructVersion);
  // --- Incremental description index (Floor 2) ---
  /** name / qualifiedName → declaration refs (target of `[[Name]]`). */
  private descByName = new Map<string, DescriptionDeclRef[]>();
  /** stable id → declaration ref (target of `[[#id]]`). */
  private descById = new Map<string, DescriptionDeclRef>();
  /** Per root: its module-subtree desc contributions (identity-cached) — patched
   *  on the same root diff that patches the symbol index. */
  private rootDescRefs = new WeakMap<ResolvedModule, DescriptionDeclRef[]>();
  /** The non-module "tail" (types/subprocesses/processes/views), rebuilt only
   *  when one of those changes (`descTailDirty`); small vs the module count. */
  private tailRefs: DescriptionDeclRef[] = [];
  private descTailDirty = true;
  /** Per-validate cascaded model used to lazily build the doc-aux maps (foreign
   *  getters `[[X]]@team`, aspect refs) — set at the start of validation and
   *  the cache cleared, so a description that uses none pays nothing. Matches
   *  the cold `collectDocAux` exactly → cold == delta. */
  private descAuxModel: ResolvedModel | undefined;
  private descAuxCache: ReturnType<typeof collectDocAux> | undefined;
  private descDocSections = new Map<string, readonly string[]>();
  /** Signature of the global aspects.set — an aspect ref `[[@@k]]` depends on it,
   *  so a change drops the description cross-diag cache. */
  private lastAspectSig: string | undefined;
  /** Signature of the decoration VOCABULARY (kind names + their field lists) —
   *  the peer of `lastAspectSig` for `@deco.field` getters. See `validateDelta`. */
  private lastDecoSig: string | undefined;
  private ensureDescAux(): ReturnType<typeof collectDocAux> {
    if (!this.descAuxCache) {
      this.descAuxCache = this.descAuxModel
        ? collectDocAux(this.descAuxModel)
        : { aspectsByQName: new Map(), fieldsByQName: new Map(), aspectSet: new Set() };
    }
    return this.descAuxCache;
  }
  /** Stable DescriptionIndex view backed by the patched maps above. */
  private readonly descIndexView: DescriptionIndex = ((self: IncrementalCompiler) => ({
    resolveId: (id: string) => self.descById.get(id),
    resolveName: (name: string) => self.descByName.get(name) ?? [],
    aspectsFor: (q: string) => self.ensureDescAux().aspectsByQName.get(q),
    fieldsFor: (q: string) => self.ensureDescAux().fieldsByQName.get(q),
    resolveAspect: (key: string, value: string | undefined) => {
      const s = self.ensureDescAux().aspectSet;
      return value !== undefined ? s.has(`${key}:${value}`) : s.has(key);
    },
    resolveDocument: (name: string) => self.descDocSections.get(name),
    // A getter, and bound through an explicit `self` because inside one `this`
    // is the object literal rather than the compiler. Lazy on purpose: the aux
    // model is reassigned on every rebuild while this view outlives them all.
    get decorations(): DecorationRegistry | undefined {
      return self.descAuxModel?.decorationRegistry;
    },
  }))(this);
  /** A process entry was added/removed this `setFile` → process diags stale. */
  private processTouched = true;
  /** Per top-level root: local (pure) diags, keyed by resolved identity. */
  private localDiagsCache = new WeakMap<ResolvedModule, readonly Diagnostic[]>();

  // --- S6 reverse-dependency state (structural edits O(changed)) ---
  /** Cascaded roots from the last `state()` — diffed by identity to find the
   *  changed / added / removed roots without walking the unchanged ones. */
  private prevRoots: readonly ResolvedModule[] = [];
  /** Per root: the symbols it defines / references (identity-cached). */
  private rootSyms = new WeakMap<ResolvedModule, { mods: string[]; ifaces: string[]; ids: string[] }>();
  private rootRefs = new WeakMap<ResolvedModule, { paths: string[]; ids: string[] }>();
  /** Per root: its cross-ref diags (aspect+description). */
  private crossDiagsByRoot = new WeakMap<ResolvedModule, readonly Diagnostic[]>();
  /** Incremental symbol index: refcounted token counts (`m:`/`i:`/`#:`) + the
   *  derived module/interface name sets passed to validation. */
  private gTokenCount = new Map<string, number>();
  private symModSet = new Set<string>();
  private symIfaceSet = new Set<string>();
  /** Symbol tokens referenced anywhere in any process body (top-level AND
   *  module-body processes) — gates both whole-process-set re-validation and the
   *  graph-edge rebuild. Rebuilt lazily from the ASSEMBLED model (which hoists
   *  body processes into `model.processes`) when the process set changes
   *  (`procRefsDirty`); deriving it from the model — not from top-level decls —
   *  is what makes a body process see a flipped symbol it references. */
  private procRefsSet = new Set<string>();
  private procRefsDirty = true;
  /** Whole-process-set diags (re-validated only when an affected symbol flips). */
  private procDiags: readonly Diagnostic[] | undefined;
  /** Whole-subprocess-set diags, gated by `subsTouchedBy`. */
  private subDiags: readonly Diagnostic[] | undefined;
  /** Symbol tokens referenced anywhere in any subprocess body — gates both
   *  subprocess re-validation and graph-edge rebuild (subprocess `do` bodies
   *  contribute edges). Rebuilt from the assembled model when the subprocess
   *  set changes (`subRefsDirty`). */
  private subRefsSet = new Set<string>();
  private subRefsDirty = true;
  /** Whole-type-set diags (reserved names + descriptions). */
  private typeDiags: readonly Diagnostic[] | undefined;
  private typeDirty = true;
  /** Instrumentation: what the last `state()` actually recomputed (for tests/bench). */
  private vstats = { indexRebuilds: 0, localRecomputes: 0, crossRecomputes: 0, procRecomputes: 0 };
  /** S4: cached dependency EDGES (a pure function of processes + structure).
   *  Reused across value edits; rebuilt only when the symbol surface or a
   *  process changed. Indexes are always rebuilt fresh (point at new objects). */
  private graphEdgesCache: { v: number; edges: DependencyEdge[] } | undefined;
  private graphEdgesDirty = true;
  private graphEdgeRebuilds = 0;
  /** A1: cached `computePresence` output — handed to `assembleModel` verbatim
   *  when no presence input changed since it was computed. Presence resolves
   *  refs through the WHOLE module path universe (unique-suffix hoisting), so
   *  the dirty signature is deliberately coarse — see the `presenceDirty`
   *  union in `setFile`. The cold path never receives this cache. */
  private presenceCache: PresenceInfo | undefined;
  private presenceDirty = true;
  /** A1: cached outputs + emitted diagnostics of the five assembly passes
   *  inside `assembleModel` (root uniqueness, scoped process/subprocess
   *  collection, flat-decl uniqueness, cascade drops) — replayed verbatim when
   *  no assembly input changed since they ran. Dirty union in `setFile`; the
   *  cold path never receives this cache. */
  private assemblyCache: AssemblyOutputs | undefined;
  private assemblyDirty = true;
  /** Module entry ids whose RESOLVED subtree declares ≥1 aspect cascade drop.
   *  Maintained beside `structSigById`; see `subtreeHasAspectDrops` for why a
   *  non-empty set forces assembly recompute on any module change. */
  private dropDeclIds = new Set<string>();
  /** Instrumentation: per-phase wall time (ms) of the last non-cached `state()`.
   *  Phase-0 attribution of the O(N) residue; ~µs of `performance.now()` overhead,
   *  no behavior change. Read via {@link _stateTimings}. */
  private timings: Record<string, number> = {};
  /** Time `fn` under `label` (accumulated), returning its result. */
  private time<T>(label: string, fn: () => T): T {
    const t0 = performance.now();
    const r = fn();
    this.timings[label] = (this.timings[label] ?? 0) + (performance.now() - t0);
    return r;
  }
  /** Last full snapshot; reused verbatim when `state()` is called again without
   *  an intervening `setFile` (e.g. a re-render loop) — then it is O(1). */
  private cachedState: CompiledState | undefined;

  private readonly parse: ParseFn;

  constructor(opts: IncrementalCompilerOptions) {
    this.parse = opts.parse;
    this.spaces = opts.spaces;
    this.nowMs = opts.nowMs;
  }

  private spaceOf = (d: { source?: { file?: string } }): QualifiedName | undefined =>
    this.spaces && d.source?.file ? this.spaces.get(d.source.file) : undefined;

  /** Replace the file→space map (v0.5). Forces a full re-resolve since space
   *  membership changes every module's registry lookup. */
  setSpaces(spaces: ReadonlyMap<string, QualifiedName> | undefined): void {
    this.spaces = spaces;
    this.forceFullRecompile();
  }

  /** Pin the instant `now` is placed against on the derived timeline. Moving it
   *  can move every lifecycle state, so it forces a full re-resolve exactly as a
   *  space change does. */
  setNow(nowMs: number | undefined): void {
    this.nowMs = nowMs;
    this.forceFullRecompile();
  }

  /**
   * Set the ambient IMPORTED types — synthetic `TypeDecl`s for `use … from …`
   * declarations, resolved against dependency packages upstream. They live in
   * the registry like local types but belong to no file, so they're stable
   * across user-file edits (imports only change on a `use`/manifest edit).
   * A change here forces a full re-resolve (an imported template stamps every
   * instance), which is rare.
   */
  setImports(types: readonly TypeDecl[]): void {
    this.imports = types;
    this.forceFullRecompile();
  }

  /** Drop every derived cache so the next `state()` rebuilds from scratch —
   *  used when an input that affects EVERY module changes (spaces, imports). */
  private forceFullRecompile(): void {
    this.registry = undefined; // rebuild registry + re-resolve all modules
    this.cachedState = undefined;
    this.presenceDirty = true; // spaces/imports feed module resolution → presence
    this.assemblyDirty = true; // …and re-resolution rebuilds every module body
    this.resetIncrementalIndexes();
    this.procDiags = undefined;
    this.typeDirty = true;
  }

  /** Drop the per-root symbol + description indexes so the next `state()`
   *  rebuilds them from scratch (used when every path may have shifted: a space
   *  remap or a registry rebuild). */
  private resetIncrementalIndexes(): void {
    this.prevRoots = [];
    this.gTokenCount.clear();
    this.symModSet.clear();
    this.symIfaceSet.clear();
    this.descByName.clear();
    this.descById.clear();
    this.tailRefs = [];
    this.descTailDirty = true;
    this.subRefsDirty = true;
    this.procRefsDirty = true;
  }

  /** Add / replace a file. Empty content removes it. Returns the id-delta. */
  setFile(path: string, content: string): CompileDelta {
    this.cachedState = undefined; // snapshot stale
    const r = this.parse(content, path);

    // 0. Surface parse/lex errors as diagnostics (matches the cold loaders),
    //    keyed by file so an edit replaces only this file's parse errors.
    if (r.errors && r.errors.length > 0) {
      this.parseDiagsByFile.set(
        path,
        r.errors.map((e) => ({ severity: "error" as const, message: e.message, source: e.span, code: e.code ?? "PARSE_ERROR", msgKey: e.msgKey, params: e.params }))
      );
    } else {
      this.parseDiagsByFile.delete(path);
    }

    // v0.9.0 §1.2: top-level `on { … }` inclusion blocks are not declarations, so
    // they never appear in the decl diff below. Track them per file and treat any
    // change as a broad invalidation (they alter plane presence globally) — an
    // inclusion edit is rare + structural, so a full recompute is acceptable and
    // keeps the incremental presence identical to cold.
    const oldOnInc = this.onInclusionsByFile.get(path) ?? [];
    const newOnInc = r.file.onInclusions ?? [];
    const onInclusionsChanged = onInclusionsKey(oldOnInc) !== onInclusionsKey(newOnInc);
    if (newOnInc.length) this.onInclusionsByFile.set(path, newOnInc);
    else this.onInclusionsByFile.delete(path);

    // 1. Content-derived ids for this file's top-level decls (declaration order).
    //    Byte-identical twins (same kind+name+content) collide on the base id —
    //    disambiguate by their ORDINAL among twins in the file (`base#1`, `#2`…).
    //    Ordinal, not source offset: a twin keeps its id when unrelated text
    //    above it shifts, so only an actual change to a twin re-resolves it.
    //    Twins are a degenerate case (usually a duplicate-decl error the
    //    validator already flags); ids are internal and never surface in the
    //    resolved model, so the suffix scheme is free to change.
    const newIds: string[] = [];
    const newEntries = new Map<string, DeclEntry>();
    const twinOrdinal = new Map<string, number>();
    for (const top of r.file.declarations) {
      // Extension blocks (§4.16) are named by their target for the delta index.
      // v0.10 `lens` is anonymous — key it by "" (the twin ordinal below
      // disambiguates a file's space + module lenses).
      const name =
        top.kind === "extension"
          ? top.decl.target.join(".")
          : "name" in top.decl
            ? top.decl.name
            : "";
      const span = top.decl.source;
      const slice = span
        ? content.slice(span.start.offset, span.end.offset)
        : JSON.stringify(top.decl);
      // The `in <Parent>` prefix (§4.16) and the born-on `on <planes>` prefix
      // (§1.2) re-scope a decl but live OUTSIDE its source span (the slice is
      // just `svc X {}`). Fold both into the id so a prefix-only edit
      // (`svc X` → `in Plat svc X`, `on europe` → `on apac`) reads as a change
      // that re-resolves the decl — not as the same declaration.
      const scoped = top.decl as {
        parentPath?: readonly string[];
        onPlanes?: readonly (readonly string[])[];
        decorations?: readonly DecorationInstance[];
      };
      const parent =
        top.kind === "module" || top.kind === "process"
          ? (scoped.parentPath ?? []).join(".")
          : "";
      const born =
        top.kind === "module" || top.kind === "process" || top.kind === "subprocess"
          ? (scoped.onPlanes ?? []).map((p) => p.join(".")).join(",")
          : "";
      // The `@deco(args)` stack is the THIRD such prefix, and the outermost of
      // them — it sits above `export` / `on` / `in`, so it is outside the slice
      // too. Left out, retyping a marker above an untouched module produced a
      // byte-identical id: nothing re-resolved, the decl kept its old
      // decorations, and the derived timeline stayed frozen at whatever was
      // typed first while the cold path moved. Each instance's own span is
      // sliced (not the whole run from the first `@` to the decl) so the block
      // form `@deco on <planes> { … }`, which stamps one shared prefix onto
      // every member, does not make an edit to one member re-resolve the rest.
      // Body-member decorations need nothing here: a field/aspect/step sits
      // INSIDE the decl span, so the slice already witnesses it.
      const decos = scoped.decorations;
      // Hashed, and skipped entirely for the undecorated majority: this runs
      // over every declaration of every `setFile`, and the rest of the id
      // already hashes its bulky part rather than carrying it raw.
      const decorated = decos?.length
        ? fnv1a(
            decos
              .map((d) =>
                d.source
                  ? content.slice(d.source.start.offset, d.source.end.offset)
                  : JSON.stringify(d),
              )
              .join(";"),
          )
        : "";
      const base = `${path} ${top.kind} ${parent}>${name} on:${born} @:${decorated} ${fnv1a(slice)}`;
      const n = twinOrdinal.get(base) ?? 0;
      twinOrdinal.set(base, n + 1);
      const id = n === 0 ? base : `${base}#${n}`;
      newIds.push(id);
      newEntries.set(id, { id, kind: top.kind, name, file: path, decl: top.decl });
    }

    // 2. Diff vs the file's previous decl ids.
    const oldIds = this.fileDeclIds.get(path) ?? [];
    const oldSet = new Set(oldIds);
    const newSet = new Set(newIds);
    const added = newIds.filter((id) => !oldSet.has(id));
    const removed = oldIds.filter((id) => !newSet.has(id));

    // A1 presence cache: presence walks the assembled tree in declaration
    // order and duplicate qualified paths overwrite last-wins, so a pure
    // REORDER of a file's retained module decls (same id set, new sequence)
    // can change presence with a ZERO id-delta. Detect it against the
    // still-unmutated store; both filters share membership + kind (kind is
    // part of the id), so an index-wise mismatch means the order moved.
    const retainedOld = oldIds.filter(
      (id) => newSet.has(id) && this.entryById.get(id)?.kind === "module"
    );
    const retainedNew = newIds.filter(
      (id) => oldSet.has(id) && newEntries.get(id)!.kind === "module"
    );
    const moduleReordered = retainedOld.some((id, i) => id !== retainedNew[i]);
    // A1 assembly cache: the scoped process/subprocess arrays follow merged-file
    // DECLARATION order (tree walk, then top-level decls in order), so a pure
    // reorder of retained decls of ANY kind (e.g. two processes swapped) changes
    // `model.processes` order with a zero id-delta. Superset of the module-only
    // check above (a reordered module subsequence forces a full-sequence
    // mismatch), computed over all kinds so process/subprocess/view swaps count.
    const retainedOldAll = oldIds.filter((id) => newSet.has(id));
    const retainedNewAll = newIds.filter((id) => oldSet.has(id));
    const declReordered = retainedOldAll.some((id, i) => id !== retainedNewAll[i]);

    // Did a `type` decl change? Check BEFORE mutating typeDeclIds.
    // An extension block (§4.16) merges into a target module's body, so it
    // affects a module resolved elsewhere — treat any extension add/remove as a
    // broad (type-level) invalidation so the per-module fast path can't serve a
    // model missing the merge. (Full incremental↔extension equivalence: TODO.)
    const extensionChanged =
      added.some((id) => newEntries.get(id)!.kind === "extension") ||
      removed.some((id) => this.extensionDeclIds.has(id));
    const typeChanged =
      this.registry === undefined ||
      extensionChanged ||
      onInclusionsChanged ||
      added.some((id) => newEntries.get(id)!.kind === "type") ||
      removed.some((id) => this.typeDeclIds.has(id));

    // 3. Apply the structural delta to the stores.
    let processTouched = false;
    let viewTouched = false;
    const removedModuleIds: string[] = [];
    const changedModulePaths: string[] = [];
    const declPath = (d: ModuleDecl, name: string): string =>
      [...((d.parentPath as readonly string[] | undefined) ?? []), name].join(".");
    for (const id of removed) {
      const old = this.entryById.get(id);
      if (old?.kind === "module") {
        this.unindexModule(id, old.decl as ModuleDecl);
        removedModuleIds.push(id);
        changedModulePaths.push(declPath(old.decl as ModuleDecl, old.name));
        if (declHasBodyProcesses(old.decl as ModuleDecl)) processTouched = true;
        if (declHasBodyViews(old.decl as ModuleDecl)) viewTouched = true;
      }
      if (old?.kind === "process" || old?.kind === "subprocess") processTouched = true;
      if (old?.kind === "view") viewTouched = true;
      this.entryById.delete(id);
      this.typeDeclIds.delete(id);
      this.extensionDeclIds.delete(id);
      this.resolvedById.delete(id);
      this.resolveDiagsById.delete(id);
    }
    for (const id of added) {
      const e = newEntries.get(id)!;
      this.entryById.set(id, e);
      if (e.kind === "type") this.typeDeclIds.add(id);
      if (e.kind === "extension") this.extensionDeclIds.add(id);
      if (e.kind === "module") {
        this.indexModule(id, e.decl as ModuleDecl);
        if (declHasBodyProcesses(e.decl as ModuleDecl)) processTouched = true;
        if (declHasBodyViews(e.decl as ModuleDecl)) viewTouched = true;
      }
      if (e.kind === "process" || e.kind === "subprocess") processTouched = true;
      if (e.kind === "view") viewTouched = true;
    }
    // Assign a stable first-seen sequence the first time we ever see this path
    // (kept even if the file later empties, so a refill restores its position).
    if (!this.fileSeq.has(path)) this.fileSeq.set(path, this.seqCounter++);
    if (newIds.length === 0) {
      this.fileDeclIds.delete(path); // emptied → no decls, but keeps its seq
    } else {
      this.fileDeclIds.set(path, newIds);
    }

    // 4. Type changed → rebuild registry. Then re-resolve only the modules
    //    whose KIND signature actually changed (S1): a type edit ripples to
    //    that kind + its subtypes, but leaves unrelated kinds untouched. On
    //    the first build there is no prior state, so resolve every module.
    let registryRebuilt = false;
    let toResolve: DeclEntry[];
    if (typeChanged) {
      const firstBuild = this.registry === undefined;
      const oldRegistry = this.registry;
      this.registryDiags = [];
      this.registry = buildRegistry(this.allTypes(), this.registryDiags, this.spaceOf);
      registryRebuilt = true;

      if (firstBuild || oldRegistry === undefined) {
        toResolve = this.orderedEntries().filter((e) => e.kind === "module");
      } else {
        // Exact closure: bare names of kinds whose stamped signature changed.
        const affected = affectedKindNames(oldRegistry, this.registry);
        const ids = new Set<string>();
        for (const name of affected) {
          for (const id of this.modulesByTypeName.get(name) ?? []) ids.add(id);
        }
        // Modules ADDED in this same edit (e.g. a file that introduces a module
        // AND a type together) aren't in any "affected kind" set — their kind
        // may be unchanged — but they still need their first resolve. Without
        // this they'd be silently dropped from the model.
        for (const id of added) {
          if (newEntries.get(id)?.kind === "module") ids.add(id);
        }
        toResolve = [...ids]
          .map((id) => this.entryById.get(id))
          .filter((e): e is DeclEntry => e !== undefined && e.kind === "module");
      }
    } else {
      toResolve = added.map((id) => newEntries.get(id)!).filter((e) => e.kind === "module");
    }
    // v0.8.2 §4.16: merge extension blocks into their target module decls BEFORE
    // resolution (mirrors resolveFile). Extensions are GLOBAL (any target), so when
    // any exist we resolve EVERY module from its MERGED decl and recompute the
    // IN_TARGET_NOT_FOUND diagnostics.
    this.extensionDiags = [];
    let mergedById: Map<string, ModuleDecl> | undefined;
    const extEntries = this.orderedEntries().filter((e) => e.kind === "extension");
    // Extensions are global (any target). When any exist — OR one was just removed
    // (extensionChanged) — resolve EVERY module from its (possibly un-merged) decl,
    // so removing the last extension REVERTS its target to cold-equivalent.
    if (extEntries.length > 0 || extensionChanged) {
      const moduleEntries = this.orderedEntries().filter((e) => e.kind === "module");
      if (extEntries.length > 0) {
        const merged = applyExtensionBlocks(
          moduleEntries.map((e) => e.decl as ModuleDecl),
          extEntries.map((e) => e.decl as ExtensionDecl),
          this.extensionDiags
        );
        mergedById = new Map<string, ModuleDecl>();
        moduleEntries.forEach((e, i) => mergedById!.set(e.id, merged[i]!));
      }
      toResolve = moduleEntries; // global effect → resolve all (merged or raw)
    }
    for (const e of toResolve) {
      const decl = mergedById?.get(e.id) ?? (e.decl as ModuleDecl);
      const diags: Diagnostic[] = [];
      this.resolvedById.set(e.id, resolveModule(decl, this.registry!, diags, this.spaceOf(decl)));
      this.resolveDiagsById.set(e.id, diags);
    }

    // S3: update the structural multiset (symbol/subprocess surface). Batch the
    // net change per signature, then a flip = a signature's presence (0 ↔ >0)
    // actually changed. This makes a pure value edit — which removes the old
    // content-id and adds a new one with the SAME structural shape — a NET zero
    // (no flip), so the symbol/desc indexes stay valid.
    const netDelta = new Map<string, number>();
    const bump = (sig: string, by: number) => netDelta.set(sig, (netDelta.get(sig) ?? 0) + by);
    // Parallel presence-input multiset (A1): same remove/re-resolve pairing, so
    // a pure value edit (old id out, same-shaped new id in) nets to zero here too.
    const planeDelta = new Map<string, number>();
    const pbump = (sig: string, by: number) => planeDelta.set(sig, (planeDelta.get(sig) ?? 0) + by);
    // A1 assembly cache: were aspect cascade drops present BEFORE this edit?
    // (Captured before the maintenance below; the union at the bottom pairs it
    // with the post-edit set so removing the last drop still recomputes once.)
    const dropsBefore = this.dropDeclIds.size > 0;
    for (const id of removedModuleIds) {
      const sig = this.structSigById.get(id);
      if (sig !== undefined) { bump(sig, -1); this.structSigById.delete(id); }
      const psig = this.planeSigById.get(id);
      if (psig !== undefined) { pbump(psig, -1); this.planeSigById.delete(id); }
      this.dropDeclIds.delete(id);
    }
    for (const e of toResolve) {
      const prev = this.structSigById.get(e.id);
      if (prev !== undefined) bump(prev, -1); // type-edit re-resolve of same id
      const m = this.resolvedById.get(e.id)!;
      const sig = structuralSig(m, (m.parentPath ?? []).join("."));
      this.structSigById.set(e.id, sig);
      bump(sig, 1);
      const pPrev = this.planeSigById.get(e.id);
      if (pPrev !== undefined) pbump(pPrev, -1);
      const psig = fnv1a(planeSigString(m, (m.parentPath ?? []).join(".")));
      this.planeSigById.set(e.id, psig);
      pbump(psig, 1);
      if (subtreeHasAspectDrops(m)) this.dropDeclIds.add(e.id);
      else this.dropDeclIds.delete(e.id);
    }
    let flipped = false;
    let structNetChanged = false;
    for (const [sig, d] of netDelta) {
      if (d === 0) continue;
      structNetChanged = true;
      const before = this.structCounts.get(sig) ?? 0;
      const after = before + d;
      if ((before === 0) !== (after === 0)) flipped = true;
      if (after <= 0) this.structCounts.delete(sig);
      else this.structCounts.set(sig, after);
    }
    let planeNetChanged = false;
    for (const d of planeDelta.values()) if (d !== 0) planeNetChanged = true;
    if (flipped) {
      this.symStructVersion++;
      // NOTE: graph edges are NOT force-dirtied here — `incrementalGraph` rebuilds
      // them only when a flipped symbol is actually referenced by a process
      // (computed in `state()` from the precise flip set).
    }
    // A1 presence cache — the dirty union. Presence is a pure function of the
    // whole module path universe (`resolveRef` hoists by unique last segment,
    // so an unrelated add/remove/rename can flip a ref's resolution), the
    // per-module plane inputs (born-on / alsoOn / membership / rosters —
    // `planeSigString`), the global inclusion blocks, and stamped template
    // bodies (presence walks the RESOLVED `body.aspects`). NET multiset change
    // — not just a 0↔>0 flip — so removing one of two identical decls (which
    // changes last-segment uniqueness) recomputes too. Conservative on doubt:
    // a registry rebuild always recomputes. A pure value edit trips none of
    // these terms and reuses the cached presence verbatim.
    if (structNetChanged || planeNetChanged || moduleReordered || onInclusionsChanged || registryRebuilt) {
      this.presenceDirty = true;
    }
    // A1 assembly cache — the dirty union. The five assembly passes are pure
    // functions of: tree structure INCLUDING duplicate-decl counts (net
    // multiset change, not just a 0↔>0 flip — a second structurally identical
    // `svc Foo` must still emit DUPLICATE_MODULE), the process/subprocess decl
    // set incl. module-body ones (`processTouched`), the view set
    // (DUPLICATE_VIEW), merged-file declaration ORDER (the scoped arrays follow
    // it), and stamped template bodies (`registryRebuilt`, which also covers
    // extension-block and `on {}`-inclusion changes). `in`-clause re-parents
    // are covered structurally: `parentPath` is folded into both the decl id
    // and `structuralSig`'s parent argument. One input NO signature witnesses:
    // `validateCascadeDrops` reads ancestor-aspect VALUE-definedness (a quoted
    // `aspect { env: "prod" }` is absent from structuralSig AND planeSigString)
    // — so while any aspect drop exists (or one was just removed), ANY module
    // change conservatively recomputes. A pure value edit in a drop-free
    // workspace trips none of these and replays the cached outputs verbatim.
    const dropsRelevant = dropsBefore || this.dropDeclIds.size > 0;
    const modulesChanged = removedModuleIds.length > 0 || toResolve.length > 0;
    if (
      structNetChanged ||
      processTouched ||
      viewTouched ||
      declReordered ||
      onInclusionsChanged ||
      registryRebuilt ||
      (dropsRelevant && modulesChanged)
    ) {
      this.assemblyDirty = true;
    }
    if (registryRebuilt) {
      // A type's stamped body can change every instance's resolved value →
      // drop the reverse-dep validation caches so they recompute against the
      // new model. (Rare; correctness over cleverness.)
      this.resetIncrementalIndexes();
      this.procDiags = undefined;
      this.subDiags = undefined;
      this.subRefsDirty = true;
      this.procRefsDirty = true;
      this.typeDirty = true;
      this.graphEdgesDirty = true;
    }
    if (processTouched) {
      this.procDiags = undefined; // process / subprocess set changed
      this.subDiags = undefined; // subprocess set / bodies may have changed
      this.subRefsDirty = true; // rebuild the subprocess-ref gate set
      this.procRefsDirty = true; // rebuild the process-ref gate set (incl. body procs)
      this.graphEdgesDirty = true; // edges depend on processes + subprocess `do` bodies
      this.descTailDirty = true; // procs/subprocs are description-index targets
    }
    // The description tail (types/subprocesses/processes/views) also shifts when
    // a module's body-subprocess set changes — captured by a structural flip.
    if (flipped) this.descTailDirty = true;
    // Views are description-index targets too.
    if (viewTouched) this.descTailDirty = true;

    for (const e of toResolve) changedModulePaths.push(declPath(e.decl as ModuleDecl, e.name));

    return {
      added,
      removed,
      registryRebuilt,
      modulesResolved: toResolve.length,
      changedModulePaths: [...new Set(changedModulePaths)],
      structural: flipped,
    };
  }

  private indexModule(id: string, decl: ModuleDecl): void {
    let set = this.modulesByTypeName.get(decl.typeName);
    if (!set) this.modulesByTypeName.set(decl.typeName, (set = new Set()));
    set.add(id);
  }

  private unindexModule(id: string, decl: ModuleDecl): void {
    const set = this.modulesByTypeName.get(decl.typeName);
    if (set) {
      set.delete(id);
      if (set.size === 0) this.modulesByTypeName.delete(decl.typeName);
    }
  }

  // --- Ordered views over the keyed store (merged-file declaration order) ---

  private orderedEntries(): DeclEntry[] {
    const files = [...this.fileDeclIds.keys()].sort(
      (a, b) => this.fileSeq.get(a)! - this.fileSeq.get(b)!
    );
    const out: DeclEntry[] = [];
    for (const file of files) {
      for (const id of this.fileDeclIds.get(file) ?? []) {
        const e = this.entryById.get(id);
        if (e) out.push(e);
      }
    }
    return out;
  }

  private orderedTypes(): TypeDecl[] {
    return this.orderedEntries().filter((e) => e.kind === "type").map((e) => e.decl as TypeDecl);
  }

  /** All registry/model types: local file types (declaration order) followed by
   *  the ambient imports — matching the cold `mergeWithImports` order (locals
   *  first, imported `TypeDecl`s appended). */
  private allTypes(): TypeDecl[] {
    const local = this.orderedTypes();
    return this.imports.length ? [...local, ...this.imports] : local;
  }

  /** Assemble the raw (pre-cascade) resolved model from the keyed store.
   *  Mirrors `resolveFile` steps 4-6 exactly via the shared `assembleModel`. */
  private materializeRaw(): { model: ResolvedModel; diagnostics: Diagnostic[] } {
    const entries = this.time("  orderedEntries", () => this.orderedEntries());
    const resolvedModules: ResolvedModule[] = [];
    const types: TypeDecl[] = [];
    const processes: ProcessDecl[] = [];
    const subprocesses: SubprocessDecl[] = [];
    const views: ViewDeclV2[] = [];
    const lenses: LensDecl[] = [];
    const decorationDecls: DecorationDecl[] = [];
    const tests: TestDecl[] = [];

    // Diagnostics: parse/lex errors first (matching the cold loaders), then
    // registry collisions, then per-module resolve diags, then assembly diags.
    const diagnostics: Diagnostic[] = [];
    for (const file of [...this.parseDiagsByFile.keys()].sort()) {
      diagnostics.push(...(this.parseDiagsByFile.get(file) ?? []));
    }
    diagnostics.push(...this.extensionDiags); // §4.16: IN_TARGET_NOT_FOUND, before registry
    diagnostics.push(...this.registryDiags);
    for (const e of entries) {
      switch (e.kind) {
        case "type":
          types.push(e.decl as TypeDecl);
          break;
        case "module": {
          const rm = this.resolvedById.get(e.id);
          if (rm) {
            resolvedModules.push(rm);
            diagnostics.push(...(this.resolveDiagsById.get(e.id) ?? []));
          }
          break;
        }
        case "process":
          processes.push(e.decl as ProcessDecl);
          break;
        case "subprocess":
          subprocesses.push(e.decl as SubprocessDecl);
          break;
        case "view":
          views.push(e.decl as ViewDeclV2);
          break;
        case "lens":
          lenses.push(e.decl as LensDecl);
          break;
        case "test":
          tests.push(e.decl as TestDecl);
          break;
        case "decoration":
          decorationDecls.push(e.decl as DecorationDecl);
          break;
      }
    }

    // Imported types live in the model too (cold `resolveFile(mergedFile)`
    // collects them), appended after locals — matching `mergeWithImports`.
    for (const t of this.imports) types.push(t);

    const registry = this.registry ?? buildRegistry([], []);
    // Decorations must reach the incremental model too, or the two paths build
    // different timelines from the same text. The axis itself is derived inside
    // `assembleModel` from the markers the model mentions, so nothing further
    // needs threading — only the `now` anchor, which is pinned so both paths
    // place it identically.
    //
    // Built with the SAME options as the cold path (`resolveFile`). Without
    // `spaceOf` every declaration keys to `::name`, so two spaces declaring the
    // same decoration collapse into one and the second is discarded as a
    // duplicate; without `onDuplicate` a real collision is dropped in silence.
    // Both diverged here, and the editor is exactly where a duplicate gets
    // typed. The equivalence harness cannot see it — it compares models through
    // JSON, and a Map serializes to `{}`.
    const decorationRegistry = buildDecorationRegistry(decorationDecls, {
      spaceOf: this.spaceOf,
      onDuplicate: (d, prev) =>
        diagnostics.push(
          makeDiag(
            "error",
            d.nameSource ?? d.source,
            prev.builtin ? "DECORATION_REDECLARES_BUILTIN" : "DUPLICATE_DECORATION",
            prev.builtin ? "resolver.decorationRedeclaresBuiltin" : "resolver.duplicateDecoration",
            { name: d.name },
          ),
        ),
    });
    const onInclusions = [...this.onInclusionsByFile.values()].flat();
    // A1: on an assembly-neutral edit hand the cached pass outputs back in
    // (pre-filled holder → passes skipped, diags replayed); when any assembly
    // input moved, pass an EMPTY holder → fresh compute fills it.
    const assemblyHolder: AssemblyCacheHolder = this.assemblyDirty
      ? {}
      : { entry: this.assemblyCache };
    const model = this.time("  assembleModel", () =>
      assembleModel(
        resolvedModules,
        {
          types,
          registry,
          processes,
          subprocesses,
          views,
          lenses,
          tests,
          decorationDecls,
          decorationRegistry,
          nowMs: this.nowMs,
          spaces: this.spaces,
          onInclusions,
        },
        diagnostics, // assembly diags (in-clause + scoped-process) appended here
        this.irCache, // reuse unchanged processes' IR (cold path passes none)
        // A1: on a presence-neutral edit hand the cached presence back in;
        // when any presence input moved, pass undefined → fresh compute.
        this.presenceDirty ? undefined : this.presenceCache,
        assemblyHolder,
      ),
    );
    this.presenceCache = model.presence;
    this.presenceDirty = false;
    this.assemblyCache = assemblyHolder.entry;
    this.assemblyDirty = false;
    return { model, diagnostics };
  }

  /** The raw (pre-cascade) resolved model + its resolution diagnostics. */
  model(): ResolvedModel {
    return this.materializeRaw().model;
  }

  /** Full compiled state — cascade + graph + validate, equivalent to
   *  viewer-core's `buildViewerState`. The correctness oracle. */
  state(): CompiledState {
    if (this.cachedState) return this.cachedState; // no edit since last snapshot
    this.timings = {};
    const { model: raw, diagnostics: resolveDiags } = this.time("materializeRaw", () => this.materializeRaw());
    const hasModules = raw.modules.length > 0;
    // `raw` always carries types + registry + processes + views — only the
    // module-instance work (cascade, graph) is gated on `hasModules`. Returning
    // EMPTY_MODEL here would drop the registry for a types-only workspace (a
    // file that declares `type` kinds but no instances yet), which the cold path
    // keeps — completion/hover need that registry mid-edit. (`applyCascade` over
    // zero modules is a no-op, so `raw` is the correct cascaded model too.)
    // The cascade is the ONLY moment field expressions still exist — it
    // literalises them, so once it returns no later pass can re-derive why a
    // palette name, cast, or reference failed. Collect as it runs or the whole
    // colour/reference diagnostic family is dark on every surface that reads
    // this state (the LSP publishes from here; so does the viewer).
    const cascadeDiags: Diagnostic[] = [];
    const cascaded = hasModules
      ? this.time("applyCascade", () => applyCascade(raw, cascadeDiags))
      : raw;
    // Patch the symbol index once (from the cascaded roots) → the exact set of
    // symbols that flipped this edit. Shared by graph + validate so each only
    // recomputes what the structural change actually touched. ALWAYS called
    // (even with no modules) so emptying the workspace removes stale symbols.
    const flipped = this.time("patchSymbolIndex", () => this.patchSymbolIndex(cascaded.modules));
    // ALWAYS run the incremental graph — even with zero modules. Gating it behind
    // `hasModules` skipped cache maintenance during an empty window: edges built
    // while a module existed survived its removal in `graphEdgesCache` (the flip
    // that should rebuild was never consulted because `incrementalGraph` wasn't
    // called), then resurfaced when an UNRELATED module re-populated the
    // workspace. Over zero modules `buildEdges` yields `[]`, so the flip-gated
    // rebuild keeps the cache coherent across the empty→non-empty transition.
    const graph = this.time("incrementalGraph", () => this.incrementalGraph(raw, flipped));
    const validationDiags = this.time("incrementalValidate", () => this.incrementalValidate(cascaded, flipped));
    const diagnostics = this.time("sortDiags", () => [
      ...resolveDiags,
      ...sortDiags([...cascadeDiags, ...validationDiags]),
    ]);
    this.cachedState = { model: cascaded, rawModel: raw, graph, diagnostics };
    return this.cachedState;
  }

  /**
   * Incremental validation (S3 + S6 reverse-deps). Produces the same diagnostic
   * SET as the cold whole-model `validate()`, with O(changed) work even for
   * STRUCTURAL edits:
   * - Roots are diffed by identity → only changed/added/removed roots are touched.
   * - The symbol index is patched (refcounted) from those roots → `flipped` is
   *   the exact set of symbols whose presence changed.
   * - LOCAL checks (names/blanks/widget) cache by root identity.
   * - CROSS-REF checks (aspect/description) re-run for a root iff it
   *   changed OR it references a flipped symbol (reverse dependency).
   * - PROCESSES re-validate only when a process changed OR a flipped symbol is
   *   referenced by some process.
   * The O(N) residue is the unavoidable snapshot assembly (walk + diagnostic
   * union + the description-index rebuild when structure moves), not recompute.
   */
  /**
   * Patch the refcounted symbol index from the roots that changed identity
   * since the last snapshot, returning the set of symbol tokens whose presence
   * actually flipped. O(changed roots), not O(N) — the basis for re-validating
   * (and re-graphing) only what a structural edit truly affected.
   */
  private patchSymbolIndex(roots: readonly ResolvedModule[]): Set<string> {
    const prevSet = new Set(this.prevRoots);
    const curSet = new Set(roots);
    const removedRoots = this.prevRoots.filter((r) => !curSet.has(r));
    const addedRoots = roots.filter((r) => !prevSet.has(r));
    this.prevRoots = roots;

    const tokensOf = (r: ResolvedModule): string[] => {
      let c = this.rootSyms.get(r);
      if (!c) {
        c = extractSymbols(r);
        this.rootSyms.set(r, c);
      }
      const out: string[] = [];
      for (const m of c.mods) out.push(`m:${m}`);
      for (const i of c.ifaces) out.push(`i:${i}`);
      for (const id of c.ids) out.push(`#:${id}`);
      return out;
    };
    const net = new Map<string, number>();
    for (const r of removedRoots) for (const t of tokensOf(r)) net.set(t, (net.get(t) ?? 0) - 1);
    for (const r of addedRoots) for (const t of tokensOf(r)) net.set(t, (net.get(t) ?? 0) + 1);
    const flipped = new Set<string>();
    for (const [t, d] of net) {
      if (d === 0) continue;
      const before = this.gTokenCount.get(t) ?? 0;
      const after = before + d;
      if (before !== after) flipped.add(t); // any count change (covers ambiguity)
      if (after <= 0) {
        this.gTokenCount.delete(t);
        if (before > 0) this.tokenSetRemove(t);
      } else {
        this.gTokenCount.set(t, after);
        if (before <= 0) this.tokenSetAdd(t);
      }
    }

    // v0.9.0 §1.5/§1.6: a process's callee/actor resolution now depends on
    // cross-decl PRESENCE — whether a callee module declares a default interface
    // (`defaultInterfaceOf`) and which membership instances exist at a place
    // (`instancesAt`). Neither shows up as a flipped DEFINED symbol above (the
    // module/interface set is unchanged), so a bare-module callee (`A > B`) or an
    // instance actor (`active > …`) would keep stale edges/diags. Diff the
    // presence-effect tokens across the changed roots and inject the reference
    // tokens the dependent processes carry, so they re-validate + re-graph.
    const presenceEffects = (rs: readonly ResolvedModule[]): Map<string, number> => {
      const acc = new Map<string, number>();
      const add = (t: string) => acc.set(t, (acc.get(t) ?? 0) + 1);
      const visit = (m: ResolvedModule, parent: readonly string[]): void => {
        const path = [...parent, m.name];
        // A default interface makes a bare `> <this module>` resolve — dependents
        // reference the module by any suffix, so flip every suffix token.
        if (m.interfaces.some((i) => i.decl.unnamed && i.decl.hasBody)) {
          for (const suf of moduleSuffixes(path)) add(`dflt:${suf}`);
        }
        // Instance names this member declares — dependents reference them as
        // single-segment callers/callees.
        for (const l of m.decl.body.aspects) collectInstanceNames(l.value, (n) => add(`inst:${n}`));
        for (const r of m.decl.body.rosters ?? []) for (const e of r.entries) if (e.instance) add(`inst:${e.instance}`);
        for (const c of m.modules) visit(c, path);
      };
      for (const r of rs) visit(r, []);
      return acc;
    };
    const before = presenceEffects(removedRoots);
    const after = presenceEffects(addedRoots);
    const changedTokens = new Set<string>([...before.keys(), ...after.keys()]);
    for (const t of changedTokens) {
      if ((before.get(t) ?? 0) === (after.get(t) ?? 0)) continue; // net-zero: unchanged
      if (t.startsWith("dflt:")) flipped.add(`m:${t.slice(5)}`); // dependents reference the module
      else if (t.startsWith("inst:")) flipped.add(`m:${t.slice(5)}`); // dependents reference the instance name
    }

    // Patch the description index's MODULE contributions from the same roots.
    const spaceOf: SpaceOf = (file) => (file ? this.spaces?.get(file) : undefined);
    for (const r of removedRoots) {
      const refs = this.rootDescRefs.get(r);
      if (refs) { for (const ref of refs) this.descRemove(ref); this.rootDescRefs.delete(r); }
    }
    for (const r of addedRoots) {
      const refs = descRefsOfModuleSubtree(r, spaceOf);
      this.rootDescRefs.set(r, refs);
      for (const ref of refs) this.descAdd(ref);
    }

    return flipped;
  }

  /** True if some process references a symbol that flipped this edit. The
   *  process-ref set is rebuilt lazily from the assembled model when the process
   *  set changed (cheap: processes are few) — and from `model.processes`, which
   *  includes body processes, so a nested process gates correctly too. */
  private procsTouchedBy(flipped: ReadonlySet<string>, model: ResolvedModel): boolean {
    if (this.procRefsDirty) {
      this.procRefsSet = new Set<string>();
      for (const p of model.processes) for (const t of extractProcRefs(p)) this.procRefsSet.add(t);
      // Resolved callers too. Process-NESTED subprocess models mirror
      // `extractProcRefs`'s recursion into `proc.subprocesses`; top-level ones
      // stay in the subprocess gate (`subRefsSet`) where they belong.
      for (const pm of model.processModels ?? [])
        for (const t of resolvedCallerRefs(pm.body)) this.procRefsSet.add(t);
      for (const sm of model.subprocessModels ?? [])
        if (sm.ownerProcess !== undefined)
          for (const t of resolvedCallerRefs(sm.body)) this.procRefsSet.add(t);
      this.procRefsDirty = false;
    }
    if (flipped.size === 0) return false;
    for (const t of flipped) if (this.procRefsSet.has(t)) return true;
    return false;
  }

  /** Like `procsTouchedBy`, but for symbols referenced in subprocess bodies.
   *  The subprocess-ref set is rebuilt lazily from the assembled model when the
   *  subprocess set changed (cheap: subprocesses are few). */
  private subsTouchedBy(flipped: ReadonlySet<string>, model: ResolvedModel): boolean {
    if (this.subRefsDirty) {
      this.subRefsSet = new Set<string>();
      for (const s of model.subprocesses) for (const t of extractProcRefs(s)) this.subRefsSet.add(t);
      for (const sm of model.subprocessModels ?? [])
        for (const t of resolvedCallerRefs(sm.body)) this.subRefsSet.add(t);
      this.subRefsDirty = false;
    }
    if (flipped.size === 0) return false;
    for (const t of flipped) if (this.subRefsSet.has(t)) return true;
    return false;
  }

  private incrementalValidate(model: ResolvedModel, flipped: ReadonlySet<string>): Diagnostic[] {
    this.vstats = { indexRebuilds: 0, localRecomputes: 0, crossRecomputes: 0, procRecomputes: 0 };
    const roots = model.modules;
    // v0.8.2 hoisting: the symbol index carries all module/interface path-suffixes
    // (uniqueness-independent membership), which can't be maintained incrementally
    // per-module. Build it fresh from the assembled model each validate — O(model),
    // same as the candidate walk — so it stays byte-identical to a cold rebuild.
    const symIdx = this.time("  buildSymbolIndex", () => buildSymbolIndex(model));
    // Lazily back the description aux maps (foreign getters / aspect refs) off
    // the current model — built only if a description actually uses one.
    this.descAuxModel = model;
    this.descAuxCache = undefined;

    // 3. Description index: MODULE entries are patched per-root in
    //    `patchSymbolIndex` (O(changed)); only the small non-module TAIL
    //    (types/subprocesses/processes/views) is rebuilt, and only when one of
    //    those changed. A structural edit that adds a module never re-walks the
    //    whole model for descriptions.
    if (this.descTailDirty) {
      for (const ref of this.tailRefs) this.descRemove(ref);
      const spaceOf: SpaceOf = (file) => (file ? this.spaces?.get(file) : undefined);
      this.tailRefs = tailDescRefs(model, spaceOf);
      for (const ref of this.tailRefs) this.descAdd(ref);
      this.descTailDirty = false;
      this.vstats.indexRebuilds = 1;
      this.typeDirty = true; // type descriptions resolve against the new index
    }
    const descIdx = this.descIndexView;
    const out: Diagnostic[] = [];

    // Non-local description dependencies the symbol-flip set can't express:
    //   - an aspect ref `[[@@k]]` depends on the GLOBAL aspects.set → drop the
    //     whole cross-diag cache when the aspects.set changes;
    //   - a foreign getter `[[X]]@@l` (and any element ref) depends on X's
    //     aspects/structure → re-validate REFERRERS when X's root re-resolves.
    // `descFlipped` augments `flipped` with the symbols DEFINED by re-resolved
    // roots so `refsAffected` naturally re-validates the roots that point at them.
    const aspectSig = this.time("  vPrepAspectSig", () => [...this.ensureDescAux().aspectSet].sort().join("|"));
    if (aspectSig !== this.lastAspectSig) {
      this.crossDiagsByRoot = new WeakMap();
      this.lastAspectSig = aspectSig;
    }
    //   - a decoration getter `@deco.field` (and `[[X]]@deco.field`) resolves
    //     against the DECORATION REGISTRY, which is global in exactly the way
    //     the aspect set is: the `decoration` is declared in one file and read
    //     in another, and editing the declaration flips no symbol in the files
    //     that read it. Without this term, renaming a decoration's field kept
    //     serving diagnostics computed against the OLD vocabulary — the delta
    //     path complained about the new name while the cold path complained
    //     about the old one. Sorted, so registry insertion order can't matter.
    const decoKinds = model.decorationRegistry?.kinds;
    const decoSig = this.time("  vPrepDecoSig", () =>
      decoKinds
        ? [...decoKinds]
            .map(
              ([name, d]) =>
                `${name}(${d.fields.map((f) => `${f.name}:${f.type}${f.of ? `<${f.of}>` : ""}`).join(",")})`,
            )
            .sort()
            .join("|")
        : "",
    );
    if (decoSig !== this.lastDecoSig) {
      this.crossDiagsByRoot = new WeakMap();
      this.typeDirty = true; // type descriptions read the same getters
      this.lastDecoSig = decoSig;
    }
    const descFlipped = new Set(flipped);
    for (const m of roots) {
      if (this.crossDiagsByRoot.has(m)) continue; // unchanged root — already cached
      const syms = extractSymbols(m);
      for (const n of syms.mods) {
        descFlipped.add(`m:${n}`);
        descFlipped.add(`i:${n}`);
      }
      for (const n of syms.ifaces) descFlipped.add(`i:${n}`);
      for (const id of syms.ids) descFlipped.add(`#:${id}`);
    }

    // 4. Per-root local (identity) + cross-ref (identity OR references a flip).
    this.time("  unionLoop", () => {
    for (const m of roots) {
      let local = this.localDiagsCache.get(m);
      if (!local) {
        local = localModuleDiagnostics([m]);
        this.localDiagsCache.set(m, local);
        this.vstats.localRecomputes++;
      }
      out.push(...local);

      let cross = this.crossDiagsByRoot.get(m);
      const refs = this.rootRefs.get(m);
      if (!cross || (refs ? refsAffected(refs, descFlipped) : true)) {
        this.vstats.crossRecomputes++;
        this.rootRefs.set(m, extractRefs(m));
        cross = [
          ...aspectRefDiagnostics(this.scopeModel([m], model), symIdx),
          ...descriptionDiagnosticsScoped([m], [], descIdx, model.spaces),
        ];
        this.crossDiagsByRoot.set(m, cross);
      }
      out.push(...cross);
    }
    });

    // 5. Types: reserved names + descriptions. Recompute on a type edit or when
    //    the description index moved.
    if (!this.typeDiags || this.typeDirty) {
      this.typeDiags = [
        ...typeNameDiagnostics(model.types),
        ...descriptionDiagnosticsScoped([], model.types, descIdx, model.spaces),
      ];
      this.typeDirty = false;
    }
    out.push(...this.typeDiags);

    // 6. Processes: re-validate the whole set only when a process changed
    //    (`procDiags` cleared) or a flipped symbol is referenced by some process.
    // Cross-step role conflicts are a pure whole-model derivation shared by the
    // process AND subprocess validators — compute at most ONCE per validate
    // (both branches often fire on the same edit), lazily so a no-process edit
    // pays nothing.
    let roleConflictsCache: ReadonlySet<string> | undefined;
    const roleConflictsOf = () => (roleConflictsCache ??= findProcessRoleConflicts(model));
    const procAffected = this.procDiags === undefined || this.procsTouchedBy(flipped, model);
    if (procAffected) {
      this.vstats.procRecomputes = model.processes.length;
      const pdiags: Diagnostic[] = [];
      const roleConflicts = roleConflictsOf();
      for (const proc of model.processes) validateProcess(proc, symIdx, model, pdiags, roleConflicts);
      validateProcessOwners(model.processModels ?? [], pdiags); // owner-TODO over the process IR
      this.procDiags = pdiags;
    }
    out.push(...(this.procDiags ?? []));

    // 6b. Subprocess bodies (callees reached only via `do`). Subprocess step
    //     refs live in their own gate set (`subRefsSet`), not `procRefsSet`, so a
    //     symbol flip that resolves/breaks a subprocess callee is detected by
    //     `subsTouchedBy`, not `procsTouchedBy`. Re-validate the (small, cheap)
    //     subprocess set on any flip — correct over clever; never goes stale.
    const subAffected =
      this.subDiags === undefined || procAffected || this.subsTouchedBy(flipped, model);
    if (subAffected) {
      const sdiags: Diagnostic[] = [];
      const roleConflicts = roleConflictsOf();
      const seenSub = new Set<string>();
      for (const sub of model.subprocesses) {
        const key = sub.source
          ? `${sub.source.file}:${sub.source.start.offset}`
          : `name:${sub.name}`;
        if (seenSub.has(key)) continue;
        seenSub.add(key);
        validateSubprocess(sub, symIdx, model, sdiags, roleConflicts);
      }
      validateProcessOwners(model.subprocessModels ?? [], sdiags); // owner-TODO over the subprocess IR
      this.subDiags = sdiags;
    }
    out.push(...(this.subDiags ?? []));

    // 7. v0.9.0 model-level plane/aspect validation. Both are WHOLE-MODEL
    //    derivations over `model.presence` (cross-decl by nature — a membership
    //    pull on one module changes another's plane), so they can't be gated by
    //    the per-root flip set. Run them fresh each validate — O(model), the same
    //    cost class as the symbol-index rebuild above — so the delta path carries
    //    the SAME diagnostics (PLANE_MISMATCH, DUPLICATE_INSTANCE, roster/shadow
    //    lints) as cold `validate()`. Both self-gate on an empty/plane-less model.
    this.time("  planeValidators", () => {
      validatePlanePresence(model, out);
      validateAspectPlacement(model, out);
      validatePartialPresence(model, out);
      validateInclusions(model, out);
      // v0.10.2 latency analysis (validateLatencyFields/Coverage) — whole-model
      // folds over interfaces + process/subprocess bodies, same cost class as the
      // plane validators. Whole-model by nature (a `latency` field on one iface
      // affects the coverage partition of every process that calls it), so they
      // can't be gated by the per-root flip set; run fresh each validate so the
      // delta path carries the SAME LATENCY_MALFORMED/LATENCY_INCOMPLETE diags as
      // cold `validate()`. Both self-gate on an untimed/latency-less model.
      validateLatencyFields(model, out);
      validateLatencyCoverage(model, out);
      // Decorations + the lifecycle reference walk. Whole-model for the same
      // reason: a decoration's window is clamped by its containers, and a
      // reference finding is a property of an EDGE between two roots, so neither
      // can be gated by the per-root flip set. Missing here, the delta path
      // silently dropped every LIFECYCLE_REF_* finding the cold path reports —
      // caught by the equivalence harness on the demo corpus.
      validateDecorations(model, out);
      validateLifecycleReferences(model, out);
    });

    return out;
  }

  private tokenSetAdd(token: string): void {
    if (token.startsWith("m:")) this.symModSet.add(token.slice(2));
    else if (token.startsWith("i:")) this.symIfaceSet.add(token.slice(2));
  }
  private tokenSetRemove(token: string): void {
    if (token.startsWith("m:")) this.symModSet.delete(token.slice(2));
    else if (token.startsWith("i:")) this.symIfaceSet.delete(token.slice(2));
  }

  // --- Incremental description index patching (mirrors buildDescriptionIndex
  //     `push`: a ref is indexed by id + by its short name + qualified name). ---
  private descAdd(ref: DescriptionDeclRef): void {
    if (ref.id) this.descById.set(ref.id, ref);
    this.descNameAdd(ref.name, ref);
    if (ref.qualifiedName !== ref.name) this.descNameAdd(ref.qualifiedName, ref);
  }
  private descRemove(ref: DescriptionDeclRef): void {
    if (ref.id && this.descById.get(ref.id) === ref) this.descById.delete(ref.id);
    this.descNameRemove(ref.name, ref);
    if (ref.qualifiedName !== ref.name) this.descNameRemove(ref.qualifiedName, ref);
  }
  private descNameAdd(key: string, ref: DescriptionDeclRef): void {
    const list = this.descByName.get(key);
    if (list) list.push(ref);
    else this.descByName.set(key, [ref]);
  }
  private descNameRemove(key: string, ref: DescriptionDeclRef): void {
    const list = this.descByName.get(key);
    if (!list) return;
    const i = list.indexOf(ref);
    if (i >= 0) list.splice(i, 1);
    if (list.length === 0) this.descByName.delete(key);
  }

  /** Instrumentation: what the last `state()` validation actually recomputed. */
  _validateStats(): { indexRebuilds: number; localRecomputes: number; crossRecomputes: number; procRecomputes: number } {
    return { ...this.vstats };
  }

  /** Instrumentation: per-phase wall time (ms) of the last non-cached `state()`.
   *  Sub-phases are indented (`  assembleModel` nests under `materializeRaw`).
   *  Phase-0 attribution of the O(N) residue. */
  _stateTimings(): Record<string, number> {
    return { ...this.timings };
  }

  /**
   * Incremental graph (S4). Edges are a pure function of processes + the
   * structural path index, so a value edit reuses the cached edges verbatim;
   * only a structural / process change rebuilds them. The path indexes are
   * rebuilt fresh each call (cheap map inserts) so they point at the current
   * module objects — `modulesByPath.get(p)` always returns a live module.
   */
  private incrementalGraph(raw: ResolvedModel, flipped: ReadonlySet<string>): DependencyGraph {
    const indexes = this.time("  buildIndexes", () => buildIndexes(raw));
    // Edges depend on processes. Rebuild only when a process changed
    // (`graphEdgesDirty`) or a flipped symbol is referenced by a process.
    // A structural edit nothing wires to reuses the edges.
    if (
      !this.graphEdgesCache ||
      this.graphEdgesDirty ||
      this.procsTouchedBy(flipped, raw) ||
      this.subsTouchedBy(flipped, raw)
    ) {
      this.graphEdgesCache = { v: 0, edges: this.time("  buildEdges", () => buildEdges(raw, indexes)) };
      this.graphEdgesDirty = false;
      this.graphEdgeRebuilds++;
    }
    return {
      edges: this.graphEdgesCache.edges,
      modulesByPath: indexes.modulesByPath,
      interfacesByPath: indexes.interfacesByPath,
      surfacesByPath: indexes.surfacesByPath,
      modulesById: indexes.modulesById,
      pathOfModule: indexes.pathOfModule,
    };
  }

  /** Instrumentation: cumulative graph-edge rebuilds (for tests/bench). */
  _graphEdgeRebuilds(): number {
    return this.graphEdgeRebuilds;
  }

  /** A minimal ResolvedModel scoping a subtree, for passes that take a model
   *  (aspect-ref walk) but only need the modules + registry + spaces. */
  private scopeModel(modules: readonly ResolvedModule[], full: ResolvedModel): ResolvedModel {
    return {
      types: [],
      registry: full.registry,
      modules,
      processes: [],
      subprocesses: [],
      views: [],
      spaces: full.spaces,
    };
  }

  /** Total modules currently resolved (for assertions/benchmarks). */
  moduleCount(): number {
    return this.resolvedById.size;
  }

  /**
   * Remove a file — equivalent to `setFile(path, "")`. Explicit API for
   * callers that find the intent clearer than an empty-content call.
   */
  deleteFile(path: string): CompileDelta {
    return this.setFile(path, "");
  }

  /**
   * Fast cascaded model without full validation — assembly + cascade only.
   * O(assembly + cascade), skipping the O(N) incremental validation pass.
   * Use for diff computations that need two models to compare but don't need
   * diagnostics. The result is structurally identical to `state().model`.
   */
  cascade(): import("./resolver.js").ResolvedModel {
    const { model } = this.materializeRaw();
    return model.modules.length > 0 ? applyCascade(model) : model;
  }

  /**
   * Clone this compiler's compiled state into a new independent instance.
   *
   * The fork starts with the SAME compiled state as the original — identical
   * resolved modules, symbol index, description index, and presence/assembly
   * caches. Calling `setFile` on the fork then re-resolves only the changed
   * files (O(changed)), making it suitable for O(modified) diffs:
   *
   * ```ts
   * const base = buildCompilerFromSources(baseFiles);
   * const head = base.fork();
   * for (const [path, content] of delta) head.setFile(path, content);
   * for (const path of deleted) head.deleteFile(path);
   * const diff = diffModels(base.cascade(), head.cascade());
   * ```
   *
   * **Thread model**: original and fork are fully independent after this call.
   * Mutations (setFile) on one never affect the other.
   *
   * **Cache policy**:
   * - Authoritative Maps/Sets are deep-copied (mutation-safe).
   * - `descByName` arrays are deep-copied (descAdd/descRemove mutate in-place).
   * - `modulesByTypeName` Sets are deep-copied (unindexModule mutates in-place).
   * - `rootDescRefs` WeakMap is pre-populated for all `prevRoots` so that
   *   `patchSymbolIndex` can correctly remove their description contributions
   *   when they are replaced by subsequent `setFile` calls.
   * - Per-root identity caches (`irCache`, `localDiagsCache`, `crossDiagsByRoot`,
   *   `rootSyms`, `rootRefs`) start empty and rebuild lazily — correct and fast
   *   because the `ResolvedModule` objects are shared by reference (WeakMap keys
   *   are still valid for the unchanged roots).
   * - Immutable snapshots (`presenceCache`, `assemblyCache`, `registry`) are
   *   shared by reference; the fork replaces its own reference on next dirty.
   */
  fork(): IncrementalCompiler {
    const copy = new IncrementalCompiler({ parse: this.parse, spaces: this.spaces });

    // --- Authoritative declaration store ---
    copy.fileSeq = new Map(this.fileSeq);
    copy.seqCounter = this.seqCounter;
    copy.fileDeclIds = new Map(this.fileDeclIds);
    copy.entryById = new Map(this.entryById);
    copy.typeDeclIds = new Set(this.typeDeclIds);
    copy.extensionDeclIds = new Set(this.extensionDeclIds);
    copy.extensionDiags = [...this.extensionDiags];
    copy.resolvedById = new Map(this.resolvedById);
    copy.resolveDiagsById = new Map(this.resolveDiagsById);
    // Deep-copy: unindexModule mutates the inner Sets.
    copy.modulesByTypeName = new Map();
    for (const [k, v] of this.modulesByTypeName) copy.modulesByTypeName.set(k, new Set(v));
    copy.registry = this.registry; // immutable; replaced when dirty
    copy.registryDiags = [...this.registryDiags];
    copy.parseDiagsByFile = new Map(this.parseDiagsByFile);
    copy.onInclusionsByFile = new Map(this.onInclusionsByFile);
    copy.imports = this.imports; // readonly; replaced via setImports

    // --- S3 incremental validation state ---
    copy.structCounts = new Map(this.structCounts);
    copy.structSigById = new Map(this.structSigById);
    copy.planeSigById = new Map(this.planeSigById);
    copy.symStructVersion = this.symStructVersion;
    // irCache: empty WeakMap bound to copy's symStructVersion (fresh IncrementalIRCache
    // from constructor). Unchanged entries rebuild lazily on first state() call — fast
    // because ProcessDecl objects are shared and the version number matches.

    // --- Description index ---
    // Deep-copy descByName arrays: descNameAdd/descRemove mutate them in-place.
    copy.descByName = new Map();
    for (const [k, v] of this.descByName) copy.descByName.set(k, [...v]);
    copy.descById = new Map(this.descById);
    // Pre-populate rootDescRefs for all prevRoots so patchSymbolIndex can remove
    // their description contributions when they are replaced by a setFile edit.
    // Without this, removedRoots' desc entries would leak into the fork's index.
    const spaceOf: SpaceOf = (file) => (file ? this.spaces?.get(file) : undefined);
    for (const r of this.prevRoots) {
      const refs = this.rootDescRefs.get(r) ?? descRefsOfModuleSubtree(r, spaceOf);
      copy.rootDescRefs.set(r, refs);
    }
    copy.tailRefs = [...this.tailRefs];
    copy.descTailDirty = this.descTailDirty;
    copy.descDocSections = new Map(this.descDocSections);
    copy.lastAspectSig = this.lastAspectSig;
    copy.lastDecoSig = this.lastDecoSig;

    // --- S6 symbol index ---
    copy.prevRoots = this.prevRoots; // readonly array; replaced (not mutated) by patchSymbolIndex
    copy.gTokenCount = new Map(this.gTokenCount);
    copy.symModSet = new Set(this.symModSet);
    copy.symIfaceSet = new Set(this.symIfaceSet);
    // rootSyms / rootRefs: empty — extractSymbols/extractRefs called lazily on first
    // patchSymbolIndex removal. Correct: the same ResolvedModule object references are
    // in prevRoots so WeakMap lookups will miss → on-demand recompute → cached.

    // --- Process / graph state ---
    copy.procRefsSet = new Set(this.procRefsSet);
    copy.procRefsDirty = this.procRefsDirty;
    copy.procDiags = this.procDiags;
    copy.subDiags = this.subDiags;
    copy.subRefsSet = new Set(this.subRefsSet);
    copy.subRefsDirty = this.subRefsDirty;
    copy.typeDiags = this.typeDiags;
    copy.typeDirty = this.typeDirty;
    copy.graphEdgesCache = this.graphEdgesCache; // immutable snapshot; replaced when dirty
    copy.graphEdgesDirty = this.graphEdgesDirty;
    copy.graphEdgeRebuilds = this.graphEdgeRebuilds;

    // --- Assembly caches (immutable snapshots; replaced on next dirty setFile) ---
    copy.presenceCache = this.presenceCache;
    copy.presenceDirty = this.presenceDirty;
    copy.assemblyCache = this.assemblyCache;
    copy.assemblyDirty = this.assemblyDirty;
    copy.dropDeclIds = new Set(this.dropDeclIds);

    // cachedState: don't copy — it references the original's timing object; the fork
    // will build its own on first state(). After a setFile the original also clears it.

    return copy;
  }
}
